import type { IntlShape } from "@formatjs/intl";
import { Typography } from "antd";
import type { TextProps } from "antd/es/typography/Text";
import { useState } from "react";
import { connect } from "react-redux";
import type { RootState } from "../../app/store";
import { EditablePencilIcon } from "../antd/EditableSelector";
import { isEmpty } from "../functions";
import { NoData } from "./NoDataComp";

export const editableTypes = {
	TEXT: "text",
	EUR_CURRENCY: "eur_currency",
};

const initialValueDisplay = (
	intl: IntlShape,
	dataType: string,
	variableValue?: string,
) => {
	if (isEmpty(variableValue)) {
		return <NoData intl={intl} />;
	}

	if (dataType == editableTypes.EUR_CURRENCY) {
		return intl.formatNumber(Number(variableValue), {
			style: "currency",
			currency: "EUR",
		});
	} else {
		return variableValue;
	}
};

export type EditableDataProps = {
	variableValue?: string;
	dataType: string;
	intl: IntlShape;
	onChange?: (variableName: string, cleanValue: any) => void;
	displayValue?: string;
	variableName?: string;
	typographyProps?: TextProps;
	readOnly?: boolean;
	keepEditing?: boolean;
	hasErrorInitial?: boolean;
	validate?: any;
	clean?: any;
	maxLength?: number;
	triggerType?: ("icon" | "text")[];
};

export const EditableText = (props: EditableDataProps) => {
	const {
		variableValue,
		dataType,
		intl,
		onChange,
		variableName,
		displayValue,
		typographyProps = null,
		readOnly = false,
		keepEditing = false,
		hasErrorInitial = false,
		validate = null,
		clean = null,
		maxLength = undefined,
		triggerType,
	} = props;

	const [hasError, setHasError] = useState(hasErrorInitial);

	const initiateChange = (newValue: string | null) => {
		const cleanValue = newValue ? (clean ? clean(newValue) : newValue) : null;

		if (validate && cleanValue) {
			const isValid = validate(cleanValue);
			if (!isValid) {
				setHasError(true);
				return;
			} else {
				hasError && setHasError(false);
			}
		}

		variableName && onChange ? onChange(variableName, cleanValue) : null;
	};

	const textValueHandle = (value: string) => {
		const valueNotEmpty = !isEmpty(value);
		const valueChanged = variableValue != value;

		if (valueChanged) {
			initiateChange(valueNotEmpty ? value : null);
		}
	};

	return (
		<Typography.Text
			className="editable-paragraph"
			// fix for negative margin in edit mode
			style={{ marginTop: "0", width: "100%", insetInlineStart: "0px" }}
			{...typographyProps}
			editable={
				readOnly
					? false
					: {
							// fix for api lacking onBlur functionality (cannot turn off editing if using state)
							editing: keepEditing || hasError || undefined,
							maxLength,
							text: isEmpty(variableValue) ? undefined : variableValue,
							icon: <EditablePencilIcon />,
							tooltip: false,
							triggerType: triggerType ? triggerType : ["icon", "text"],
							enterIcon: null,
							onChange: (value) => textValueHandle(value),
						}
			}
		>
			{displayValue ?? initialValueDisplay(intl, dataType, variableValue)}
		</Typography.Text>
	);
};

export const EditableDataConnected = connect((state: RootState) => ({
	intl: state.intl.datetimeIntl,
}))(EditableText);
